import { useEffect, useRef, useState } from 'react';
import type { CheckVerdict } from '../../../shared/protocol.ts';
import type { TailClip } from '../lib/recorder.ts';
import { audioVerdict, mountClip, videoVerdict } from '../lib/replay.ts';
import type { CheckRun, StudioEngine } from '../lib/studio.ts';
import { Icon } from './common.tsx';

/**
 * Plays back the last few seconds of every recorded source *together*, aligned on
 * their capture clocks, so a participant can see and hear that everything is working
 * (and that their sources are in sync with each other).
 */
export function ReplayModal({ engine, run }: { engine: StudioEngine; run: CheckRun }) {
  const elements = useRef(new Map<string, HTMLMediaElement>());
  const starts = useRef(new Map<string, number>());
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  /** The browser refused to start playback without a fresh click (Safari, after the test's countdown). */
  const [blocked, setBlocked] = useState(false);
  const [verdicts, setVerdicts] = useState(new Map<string, CheckVerdict>());
  const [shared, setShared] = useState<'no' | 'sending' | 'sent'>('no');
  const [error, setError] = useState('');

  // The WAV is the master audio; mute the scratch audio inside camera files to avoid doubling.
  const hasWav = run.clips.some((c) => c.audioOnly);

  useEffect(() => {
    if (run.status !== 'ready') return;
    let cancelled = false;
    (async () => {
      try {
        for (const clip of run.clips) {
          const el = elements.current.get(clip.sourceId);
          if (!el) continue;
          starts.current.set(clip.sourceId, await mountClip(clip, el));
        }
        if (cancelled) return;
        setReady(true);
        void playSynced();
        const v = new Map<string, CheckVerdict>();
        await Promise.all(
          run.clips.map(async (clip) => {
            const el = elements.current.get(clip.sourceId);
            if (clip.audioOnly) v.set(clip.sourceId, audioVerdict(clip));
            else if (el) v.set(clip.sourceId, await videoVerdict(clip, el as HTMLVideoElement, expectedFps(engine, clip)));
          }),
        );
        if (cancelled) return;
        setVerdicts(v);
        if (run.reason === 'host' && run.checkId) {
          setShared('sending');
          await engine.shareCheck(run, v);
          setShared('sent');
        }
      } catch (e) {
        setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      for (const el of elements.current.values()) {
        el.pause();
        if (el.src.startsWith('blob:')) URL.revokeObjectURL(el.src);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.status, run.id]);

  /**
   * For Replay clicks: touch every element inside the click itself, which is what Safari needs to let them play
   * later; the synced start that follows waits for seeks, which takes it outside the click.
   */
  function replayFromClick() {
    for (const el of elements.current.values()) {
      el.play().catch(() => {});
      el.pause();
    }
    void playSynced();
  }

  /** Seek every clip to the same wall-clock instant, then start them together. */
  async function playSynced() {
    const clips = run.clips.filter((c) => elements.current.has(c.sourceId));
    if (!clips.length) return;
    const t0 = Math.max(...clips.map((c) => c.wallStartLocalMs));
    for (const c of clips) {
      const el = elements.current.get(c.sourceId)!;
      el.currentTime = (starts.current.get(c.sourceId) ?? 0) + (t0 - c.wallStartLocalMs) / 1000;
    }
    // Wait for every seek to land (bounded – a missed event must never hang the check).
    await Promise.all(
      clips.map((c) => {
        const el = elements.current.get(c.sourceId)!;
        if (!el.seeking && el.readyState >= 2) return null;
        return Promise.race([new Promise((r) => el.addEventListener('seeked', r, { once: true })), new Promise((r) => setTimeout(r, 2500))]);
      }),
    );
    setPlaying(true);
    setBlocked(false);
    const started = await Promise.all(
      clips.map((c) =>
        elements.current
          .get(c.sourceId)!
          .play()
          .then(
            () => true,
            (e: DOMException) => e.name !== 'NotAllowedError',
          ),
      ),
    );
    if (started.includes(false)) {
      // Never play half the sources: stop everything and ask for a click, which the browser will allow.
      console.info('[takehouse] replay needs a click to play (autoplay blocked)');
      for (const c of clips) elements.current.get(c.sourceId)?.pause();
      setPlaying(false);
      setBlocked(true);
      return;
    }
    const shortest = Math.min(...clips.map((c) => c.seconds - (t0 - c.wallStartLocalMs) / 1000));
    setTimeout(() => {
      for (const c of clips) elements.current.get(c.sourceId)?.pause();
      setPlaying(false);
    }, Math.max(500, shortest * 1000));
  }

  const title =
    run.reason === 'host' ? `${run.requestedBy ?? 'The host'} asked for a check` : run.reason === 'preflight' ? 'Test recording' : 'Instant replay';
  const allOk = verdicts.size > 0 && [...verdicts.values()].every((v) => v.ok);

  return (
    <div className="modal-back" onClick={(e) => e.target === e.currentTarget && engine.closeCheck()}>
      <div className="modal" role="dialog" aria-label={title}>
        <header>
          <span className="ficon">
            <Icon name="replay" size={17} />
          </span>
          <div className="col grow" style={{ gap: 1 }}>
            <h2>{title}</h2>
            <span className="muted small">
              {run.status === 'capturing'
                ? 'Recording and reading back from disk…'
                : run.reason === 'preflight'
                  ? 'Exactly what was saved to disk during the test, played back in sync.'
                  : 'The latest seconds already saved to disk, from every source you’re recording, played back in sync.'}
            </span>
          </div>
          {verdicts.size > 0 && (
            <span className={`pill ${allOk ? 'ok' : 'warn'}`}>
              {allOk ? '✓ Everything looks good' : <><Icon name="warn" size={12} /> Some sources need attention</>}
            </span>
          )}
          {shared === 'sending' && <span className="pill">Sending to host…</span>}
          {shared === 'sent' && <span className="pill ok">Sent to host</span>}
          <button className="ghost icon" onClick={() => engine.closeCheck()} aria-label="Close">
            <Icon name="close" />
          </button>
        </header>
        <div className="content">
          {run.status === 'capturing' &&
            (run.countdown != null && run.countdown > 0 ? (
              <div className="capture">
                <div className="countdown">{run.countdown}</div>
                <span className="muted">Recording a short test of every source set to Record. Talk, move, show your screen…</span>
              </div>
            ) : (
              <div className="capture">
                <span className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
                <span className="muted">Reading the last seconds back from disk…</span>
              </div>
            ))}
          {run.status === 'failed' && (
            <div className="alert bad">
              <Icon name="warn" size={16} /> {run.message ?? 'The check failed.'}
            </div>
          )}
          {error && (
            <div className="alert bad">
              <Icon name="warn" size={16} /> {error}
            </div>
          )}
          {run.status === 'ready' && blocked && (
            <button className="primary big replay-tap" onClick={replayFromClick}>
              <Icon name="play" size={16} /> Tap to hear the replay
              <span className="hint">Your browser only plays sound after a click</span>
            </button>
          )}
          {run.status === 'ready' && (
            <div className="replay-grid">
              {run.clips.map((clip) => (
                <ClipTile
                  key={clip.sourceId}
                  clip={clip}
                  muted={!clip.audioOnly && hasWav && clip.kind === 'camera'}
                  verdict={verdicts.get(clip.sourceId)}
                  register={(el) => (el ? elements.current.set(clip.sourceId, el) : elements.current.delete(clip.sourceId))}
                />
              ))}
            </div>
          )}
        </div>
        {run.status !== 'capturing' && (
          <footer>
            {run.status === 'ready' && (
              <button className="primary" disabled={!ready || playing} onClick={replayFromClick}>
                <Icon name={playing ? 'wave' : 'play'} size={15} /> {playing ? 'Playing…' : 'Replay'}
              </button>
            )}
            <span className="grow" />
            {run.reason !== 'host' && (
              <button onClick={() => void engine.runCheck(run.reason === 'preflight' ? 'preflight' : 'auto')}>Run again</button>
            )}
            <button onClick={() => engine.closeCheck()}>Done</button>
          </footer>
        )}
      </div>
    </div>
  );
}

function ClipTile({ clip, muted, verdict, register }: { clip: TailClip; muted: boolean; verdict?: CheckVerdict; register: (el: HTMLMediaElement | null) => void }) {
  return (
    <div className="replay-item">
      {clip.audioOnly ? (
        <div className="audio-viz">
          <Icon name="wave" size={44} />
          <audio ref={register} preload="auto" />
        </div>
      ) : (
        <video ref={register} playsInline muted={muted} preload="auto" />
      )}
      <div className="meta small">
        <div className="row between">
          <strong className="ellipsis">{clip.label}</strong>
          <span className="faint tnum">{clip.seconds.toFixed(1)} s</span>
        </div>
        {verdict ? (
          <span className={`pill verdict ${verdict.ok ? 'ok' : 'bad'}`}>
            {verdict.ok ? '✓' : <Icon name="warn" size={12} />} {verdict.summary}
          </span>
        ) : (
          <span className="pill">
            <span className="spinner" style={{ width: 10, height: 10, borderWidth: 1.5 }} /> Checking…
          </span>
        )}
      </div>
    </div>
  );
}

function expectedFps(engine: StudioEngine, clip: TailClip) {
  const source = engine.sources.byId(clip.sourceId);
  return (source && engine.sources.videoFor(source)?.fps) || 30;
}
